// Home 首屏数据的本地快照协议。
//
// boot 与 React 首页共用本文件：boot 在主 bundle 执行前读取快照并预热其中的媒体，
// React 则把同一份数据作为 SWR 第一页 fallbackData 和本轮列表的稳定前缀立即回显。
// 保持零 import，避免预取 boot 的独立构建把主工程依赖图带进来。

export const HOME_FIRST_SCREEN_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000

const HOME_FIRST_SCREEN_CACHE_VERSION = 1
const HOME_FIRST_SCREEN_CACHE_KEY_PREFIX = 'tipsy-home-first-screen-data:v1:'
const HOME_ACTIVITY_LIST_CACHE_VERSION = 1
const HOME_ACTIVITY_LIST_CACHE_KEY_PREFIX =
  'tipsy-home-first-screen-activities:v1:'
const VOLATILE_REQUEST_FIELDS = ['session_id']

export type HomeFirstScreenCacheDevice = 'desktop' | 'mobile'

export type HomeFirstScreenCacheMatch = {
  device: HomeFirstScreenCacheDevice
  requestKey: string
  storageKey: string
}

export type HomeFirstScreenCacheSnapshot<T = unknown> = {
  version: typeof HOME_FIRST_SCREEN_CACHE_VERSION
  device: HomeFirstScreenCacheDevice
  requestKey: string
  savedAt: number
  expiresAt: number
  items: T[]
}

export type HomeActivityListCacheSnapshot<T = unknown> = {
  version: typeof HOME_ACTIVITY_LIST_CACHE_VERSION
  savedAt: number
  expiresAt: number
  activities: T[]
}

export type HomeFirstScreenCacheStorage = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

type HomeFirstScreenCacheHost = typeof globalThis & {
  __tipsyHomeFirstScreenCacheV1?: Map<string, HomeFirstScreenCacheSnapshot>
}

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') {
    const serialized = JSON.stringify(value)
    return serialized === undefined ? String(value) : serialized
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`
  }
  const record = value as Record<string, unknown>
  const keys = Object.keys(record)
    .filter((key) => !VOLATILE_REQUEST_FIELDS.includes(key))
    .sort()
  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(',')}}`
}

const normalizeEndpoint = (endpoint: string): string => {
  try {
    return new URL(endpoint).pathname
  } catch {
    return endpoint
  }
}

const getMemoryCache = (): Map<string, HomeFirstScreenCacheSnapshot> => {
  const host = globalThis as HomeFirstScreenCacheHost
  if (!host.__tipsyHomeFirstScreenCacheV1) {
    host.__tipsyHomeFirstScreenCacheV1 = new Map()
  }
  return host.__tipsyHomeFirstScreenCacheV1
}

const getBrowserStorage = (): HomeFirstScreenCacheStorage | undefined => {
  try {
    if (typeof window === 'undefined') return undefined
    return window.localStorage
  } catch {
    return undefined
  }
}

const isDevice = (value: unknown): value is HomeFirstScreenCacheDevice =>
  value === 'desktop' || value === 'mobile'

const parseSnapshot = (
  raw: string
): HomeFirstScreenCacheSnapshot | undefined => {
  try {
    const value = JSON.parse(raw) as Partial<HomeFirstScreenCacheSnapshot>
    if (
      value.version !== HOME_FIRST_SCREEN_CACHE_VERSION ||
      !isDevice(value.device) ||
      typeof value.requestKey !== 'string' ||
      !value.requestKey ||
      typeof value.savedAt !== 'number' ||
      !Number.isFinite(value.savedAt) ||
      typeof value.expiresAt !== 'number' ||
      !Number.isFinite(value.expiresAt) ||
      value.expiresAt <= value.savedAt ||
      !Array.isArray(value.items) ||
      value.items.length === 0
    ) {
      return undefined
    }
    return value as HomeFirstScreenCacheSnapshot
  } catch {
    return undefined
  }
}

const parseActivityListSnapshot = (
  raw: string
): HomeActivityListCacheSnapshot | undefined => {
  try {
    const value = JSON.parse(raw) as Partial<HomeActivityListCacheSnapshot>
    if (
      value.version !== HOME_ACTIVITY_LIST_CACHE_VERSION ||
      typeof value.savedAt !== 'number' ||
      !Number.isFinite(value.savedAt) ||
      typeof value.expiresAt !== 'number' ||
      !Number.isFinite(value.expiresAt) ||
      value.expiresAt <= value.savedAt ||
      !Array.isArray(value.activities)
    ) {
      return undefined
    }
    return value as HomeActivityListCacheSnapshot
  } catch {
    return undefined
  }
}

const getActivityListStorageKey = (userId: string) =>
  HOME_ACTIVITY_LIST_CACHE_KEY_PREFIX + (userId || 'anonymous')

export const selectHomeFirstScreenCacheItems = <T>(
  items: readonly T[],
  maxItems: number
): T[] => {
  if (!Number.isFinite(maxItems) || maxItems <= 0) return []
  return items.slice(0, Math.floor(maxItems))
}

export const mergeHomeFirstScreenCacheItems = <T>(
  cachedItems: readonly T[],
  networkItems: readonly T[],
  getIdentity: (item: T) => string | number | undefined
): T[] => {
  const result: T[] = []
  const seen = new Set<string | number>()

  for (const item of [...cachedItems, ...networkItems]) {
    const identity = getIdentity(item)
    if (identity === undefined) {
      result.push(item)
      continue
    }
    if (seen.has(identity)) continue
    seen.add(identity)
    result.push(item)
  }

  return result
}

export const createHomeFirstScreenCacheMatch = ({
  endpoint,
  body,
  userId,
  device,
}: {
  endpoint: string
  body: unknown
  userId: string
  device: HomeFirstScreenCacheDevice
}): HomeFirstScreenCacheMatch => ({
  device,
  requestKey: `${normalizeEndpoint(endpoint)}\n${stableStringify(body)}\n${userId}`,
  storageKey: HOME_FIRST_SCREEN_CACHE_KEY_PREFIX + device,
})

export const readHomeFirstScreenCache = <T>(
  match: HomeFirstScreenCacheMatch,
  now: number = Date.now(),
  storage: HomeFirstScreenCacheStorage | undefined = getBrowserStorage()
): HomeFirstScreenCacheSnapshot<T> | undefined => {
  const memoryCache = getMemoryCache()
  let snapshot = memoryCache.get(match.storageKey)

  if (!snapshot && storage) {
    try {
      const raw = storage.getItem(match.storageKey)
      snapshot = raw ? parseSnapshot(raw) : undefined
      if (snapshot) memoryCache.set(match.storageKey, snapshot)
    } catch {
      return undefined
    }
  }

  if (!snapshot) return undefined
  if (snapshot.expiresAt <= now) {
    memoryCache.delete(match.storageKey)
    try {
      storage?.removeItem(match.storageKey)
    } catch {
      // 过期清理失败不影响回退网络。
    }
    return undefined
  }
  if (
    snapshot.device !== match.device ||
    snapshot.requestKey !== match.requestKey
  ) {
    return undefined
  }
  return snapshot as HomeFirstScreenCacheSnapshot<T>
}

export const writeHomeFirstScreenCache = <T>(
  match: HomeFirstScreenCacheMatch,
  items: readonly T[],
  now: number = Date.now(),
  storage: HomeFirstScreenCacheStorage | undefined = getBrowserStorage()
): boolean => {
  if (!storage || items.length === 0) return false
  const snapshot: HomeFirstScreenCacheSnapshot<T> = {
    version: HOME_FIRST_SCREEN_CACHE_VERSION,
    device: match.device,
    requestKey: match.requestKey,
    savedAt: now,
    expiresAt: now + HOME_FIRST_SCREEN_CACHE_TTL_MS,
    items: [...items],
  }

  try {
    storage.setItem(match.storageKey, JSON.stringify(snapshot))
    getMemoryCache().set(
      match.storageKey,
      snapshot as HomeFirstScreenCacheSnapshot
    )
    return true
  } catch {
    // 隐私模式、禁用存储或 quota exceeded 时保持原网络路径。
    return false
  }
}

// 活动接口返回原始多语言配置；这里只缓存原始列表。活动时间窗、用户预览
// 白名单和 Home/World 展示范围仍由渲染层按当前上下文实时判断。
export const readHomeActivityListCache = <T>(
  userId: string,
  now: number = Date.now(),
  storage: HomeFirstScreenCacheStorage | undefined = getBrowserStorage()
): HomeActivityListCacheSnapshot<T> | undefined => {
  if (!storage) return undefined
  const storageKey = getActivityListStorageKey(userId)

  try {
    const raw = storage.getItem(storageKey)
    const snapshot = raw ? parseActivityListSnapshot(raw) : undefined
    if (!snapshot) return undefined
    if (snapshot.expiresAt <= now) {
      storage.removeItem(storageKey)
      return undefined
    }
    return snapshot as HomeActivityListCacheSnapshot<T>
  } catch {
    return undefined
  }
}

export const writeHomeActivityListCache = <T>(
  userId: string,
  activities: readonly T[],
  now: number = Date.now(),
  storage: HomeFirstScreenCacheStorage | undefined = getBrowserStorage()
): boolean => {
  if (!storage) return false
  const snapshot: HomeActivityListCacheSnapshot<T> = {
    version: HOME_ACTIVITY_LIST_CACHE_VERSION,
    savedAt: now,
    expiresAt: now + HOME_FIRST_SCREEN_CACHE_TTL_MS,
    activities: [...activities],
  }

  try {
    storage.setItem(getActivityListStorageKey(userId), JSON.stringify(snapshot))
    return true
  } catch {
    return false
  }
}
